// Global Instructions Rule Applied!

import React, { useState, useCallback, useMemo, forwardRef, useImperativeHandle, useEffect } from 'react'
import { faPlus } from '@fortawesome/free-solid-svg-icons'
import { FormTable } from '../FormTable/index.tsx'
import { InputManager } from '../InputManager/index.ts'
import { FieldTypeEnum } from '../InputManager/types.ts'
import { CompanyTeamEntryFields } from '../../../state_manager/data_models/company_team_entry.ts'
import {
  RELATIONSHIP_CATEGORY_INTRODUCED_BY,
  RELATIONSHIP_CATEGORY_SOURCED_BY
} from '../../../state_manager/globals.ts'
import { useLookups } from '../../../app_modules/global_configs/hooks/useLookups.ts'
import { useSelector } from 'react-redux'
import { ACTION_TYPE_INSERT } from '../../../state_manager/globals.ts'
import { createCustomValidationRule, validateRecord, validateAndShowErrors } from '../FormTable/validation.ts'
import { getColumnConfig } from '../CompanyTeamTable/utils/fieldConfigs.tsx'
import { toast } from 'react-toastify'
import { TOAST_CONFIG } from '../FormTable/constants.ts'

// Helper function to check if an introduction would be a duplicate
const isDuplicateIntroduction = (newIntro, existingIntroductions, excludeKey = null) => {
  const newCompanyId = (newIntro.companyId || '').trim()
  const newCategoryId = (newIntro.categoryId || '').trim()

  // Don't check for duplicates if required fields are empty
  if (!newCompanyId || !newCategoryId) {
    return false
  }

  return existingIntroductions.some((intro) => {
    // Skip the introduction being edited
    if (excludeKey && intro.key === excludeKey) {
      return false
    }

    const existingCompanyId = (intro.companyId || '').trim()
    const existingCategoryId = (intro.categoryId || '').trim()

    // Check for duplicates by: same companyId and categoryId
    const companyMatch = newCompanyId === existingCompanyId
    const categoryMatch = newCategoryId === existingCategoryId

    return companyMatch && categoryMatch
  })
}

/**
 * IntroductionsTable Component
 * Similar to CompanyTeamTable but simpler - only Company and Role columns
 * Used for managing introductions (Introduced by / Sourced by relationships)
 */
/**
 * @typedef {object} IntroductionsTableProps
 * @property {'new' | 'existing'} [mode]
 * @property {string | null} [contactId]
 * @property {any[]} [initialIntroductions]
 * @property {((introductions: any[]) => void) | null} [onChange]
 * @property {((introduction: any, allIntroductions: any[]) => unknown) | null} [onSave]
 * @property {((introduction: any, allIntroductions: any[]) => unknown) | null} [onRemove]
 * @property {string | null} [recordId]
 * @property {((getApiData: () => any[]) => void) | null} [getCurrentTeamMembers] - registers a getter for the rows in API shape
 * @property {boolean} [readOnly]
 * @property {string} [title]
 */
export const IntroductionsTable = forwardRef(
  /**
   * @param {IntroductionsTableProps} props
   * @param {React.ForwardedRef<any>} ref
   */
  (
    {
      mode = 'new',
      contactId = null,
      initialIntroductions = [],
      onChange = null,
      onSave = null,
      onRemove = null,
      recordId = null,
      getCurrentTeamMembers = null,
      readOnly = false,
      title = 'Introductions made'
    },
    ref
  ) => {
    const { getLookupOptions } = useLookups()
    const relationshipCategoriesRaw = useSelector((state) => state.globalConfigs?.relationship_categories || [])
    const relationshipCategories = useMemo(() => relationshipCategoriesRaw || [], [relationshipCategoriesRaw])
    const [editingRowKey, setEditingRowKey] = useState(null)
    const [savingRowKey, setSavingRowKey] = useState(null)
    const [deletingRowKey, setDeletingRowKey] = useState(null)
    const [isAddingNew, setIsAddingNew] = useState(false)
    const [originalIntroductionData, setOriginalIntroductionData] = useState(null) // Store original data for cancel
    const [validationErrors, setValidationErrors] = useState({}) // Track validation errors by field
    const [firstErrorFieldKey, setFirstErrorFieldKey] = useState(null) // Track first error field for auto-focus: { rowKey, fieldKey }
    const [introductions, setIntroductions] = useState(() => {
      // Transform initial introductions to table format
      if (!initialIntroductions || !Array.isArray(initialIntroductions)) return []
      return initialIntroductions.map((intro) => ({
        key: intro[CompanyTeamEntryFields.contact_accounts__id] || `intro-${crypto.randomUUID()}`,
        companyId: intro[CompanyTeamEntryFields.contact_accounts__account_id] || '',
        companyName: intro[CompanyTeamEntryFields.prog__account__name] || '',
        categoryId: intro[CompanyTeamEntryFields.contact_accounts__relationship_category_id] || '',
        categoryLabel: ''
      }))
    })

    const introductionsRef = React.useRef(introductions)
    React.useEffect(() => {
      introductionsRef.current = introductions
    }, [introductions])

    // Use ref to track if we're updating from parent (to prevent infinite loops)
    const isUpdatingFromParentRef = React.useRef(false)
    // Cancelling the only draft row from an empty table clears local state before parent props update; sync must not re-apply stale initialIntroductions in that window
    const cancelledDraftToEmptyRef = React.useRef(false)

    // Get relationship category options (filtered to only Introduced by and Sourced by, sorted ascending)
    const categoryOptions = useMemo(() => {
      // First try to get from relationship_categories Redux state (more reliable)
      if (relationshipCategories && relationshipCategories.length > 0) {
        const filtered = relationshipCategories
          .filter(
            (cat) => cat.id === RELATIONSHIP_CATEGORY_INTRODUCED_BY || cat.id === RELATIONSHIP_CATEGORY_SOURCED_BY
          )
          .map((cat) => ({
            value: cat.id,
            label: cat.category || cat.name || '',
            id: cat.id,
            category: cat.category || cat.name || ''
          }))
        // Sort ascending by label
        return filtered.sort((a, b) => {
          const labelA = a.label || ''
          const labelB = b.label || ''
          return labelA.localeCompare(labelB)
        })
      }

      // Fallback to lookup options if relationship_categories not available
      const allOptions = getLookupOptions('Contact Relationship Category') || []
      const filtered = allOptions.filter(
        (opt) => opt.value === RELATIONSHIP_CATEGORY_INTRODUCED_BY || opt.value === RELATIONSHIP_CATEGORY_SOURCED_BY
      )
      // Sort ascending by label
      return filtered.sort((a, b) => {
        const labelA = a.label || a.name || ''
        const labelB = b.label || b.name || ''
        return labelA.localeCompare(labelB)
      })
    }, [relationshipCategories, getLookupOptions])

    // Create validation rules for introductions
    const introductionValidationRules = useMemo(
      () => [
        createCustomValidationRule(
          'Company',
          (value, fieldName, record) => {
            const companyId = record.companyId
            if (!companyId || companyId === '') {
              return 'Company is required'
            }
            return null
          },
          'companyId'
        ),
        createCustomValidationRule(
          'Role',
          (value, fieldName, record) => {
            const categoryId = record.categoryId
            if (!categoryId || categoryId === '') {
              return 'Role is required'
            }
            return null
          },
          'categoryId'
        )
      ],
      []
    )

    // Track which companies have been looked up to prevent infinite loops

    // Update introductions when initialIntroductions changes (only if different)
    const prevInitialIntroductionsRef = React.useRef(initialIntroductions)
    React.useEffect(() => {
      let resetParentFlagTimeoutId = null

      const hasChanged = JSON.stringify(prevInitialIntroductionsRef.current) !== JSON.stringify(initialIntroductions)
      // Don't overwrite local state if we're currently adding a new row or editing
      if (hasChanged && initialIntroductions && Array.isArray(initialIntroductions) && !isAddingNew && !editingRowKey) {
        // Local already [] after cancel while props still hold the draft — do not sync stale rows back in
        if (cancelledDraftToEmptyRef.current && introductions.length === 0 && initialIntroductions.length > 0) {
          return () => {
            if (resetParentFlagTimeoutId != null) clearTimeout(resetParentFlagTimeoutId)
          }
        }
        // Check if the previous value had data (to distinguish between initialization and API refresh)
        const prevHadData =
          prevInitialIntroductionsRef.current &&
          Array.isArray(prevInitialIntroductionsRef.current) &&
          prevInitialIntroductionsRef.current.length > 0 &&
          prevInitialIntroductionsRef.current.some(
            (intro) =>
              intro[CompanyTeamEntryFields.contact_accounts__account_id] &&
              intro[CompanyTeamEntryFields.contact_accounts__relationship_category_id]
          )

        // Check if there are any local introductions with data that aren't in initialIntroductions
        // (e.g., newly saved entries that haven't been reflected in initialIntroductions yet)
        const hasLocalData = introductions.some((intro) => intro.companyId && intro.categoryId)
        const initialHasData =
          initialIntroductions.length > 0 &&
          initialIntroductions.some(
            (intro) =>
              intro[CompanyTeamEntryFields.contact_accounts__account_id] &&
              intro[CompanyTeamEntryFields.contact_accounts__relationship_category_id]
          )

        // If initialIntroductions is empty, check if we should clear or preserve:
        // - If previous value had data and now it's empty, this is an API refresh clearing data - clear the table
        // - If previous value was also empty, this might be initialization - preserve local state if it has unsaved changes
        if (initialIntroductions.length === 0) {
          if (prevHadData) {
            // Previous had data, now empty - this is an API refresh, clear the table
            // Don't check for unsaved changes - force clear when API returns empty after having data
            // Continue to sync (don't return early)
          } else if (hasLocalData) {
            // Previous was also empty but we have local data - preserve local state (might be unsaved changes)
            return
          } else {
            // Both empty and no local data - sync to clear any stale state
            // Continue to sync (don't return early)
          }
        } else {
          // initialIntroductions has data
          // If we have local data but initialIntroductions doesn't have that data, preserve local state
          if (hasLocalData && !initialHasData) {
            return
          }

          // Also check if any local intro with data isn't in initialIntroductions
          const initialIntroKeys = new Set(
            initialIntroductions.map((intro) => intro[CompanyTeamEntryFields.contact_accounts__id])
          )
          const hasUnsavedChanges = introductions.some((intro) => {
            const hasData = intro.companyId && intro.categoryId
            const isNewEntry = intro.key.startsWith('new-')
            const notInInitial = intro.key && !initialIntroKeys.has(intro.key)
            return hasData && (isNewEntry || notInInitial)
          })

          if (hasUnsavedChanges) {
            return
          }
        }

        isUpdatingFromParentRef.current = true
        // Update prevInitialIntroductionsRef BEFORE transforming to ensure next change detection works correctly
        prevInitialIntroductionsRef.current = initialIntroductions
        const transformed = initialIntroductions.map((intro) => {
          const companyId = intro[CompanyTeamEntryFields.contact_accounts__account_id] || ''
          // Company name comes from the API response
          const companyName = intro[CompanyTeamEntryFields.prog__account__name] || ''
          return {
            key: intro[CompanyTeamEntryFields.contact_accounts__id] || `intro-${crypto.randomUUID()}`,
            companyId,
            companyName,
            categoryId: intro[CompanyTeamEntryFields.contact_accounts__relationship_category_id] || '',
            categoryLabel:
              categoryOptions.find(
                (opt) => opt.value === intro[CompanyTeamEntryFields.contact_accounts__relationship_category_id]
              )?.label || ''
          }
        })
        setIntroductions(transformed)

        // Reset flag after state update
        resetParentFlagTimeoutId = window.setTimeout(() => {
          isUpdatingFromParentRef.current = false
        }, 0)
      }

      return () => {
        if (resetParentFlagTimeoutId != null) clearTimeout(resetParentFlagTimeoutId)
      }
    }, [initialIntroductions, categoryOptions, isAddingNew, editingRowKey, introductions])

    React.useEffect(() => {
      if (!initialIntroductions?.length) {
        cancelledDraftToEmptyRef.current = false
      }
    }, [initialIntroductions])

    // Notify parent of changes (only when introductions actually change, not on every render)
    const prevIntroductionsRef = React.useRef(introductions)
    const onChangeRef = React.useRef(onChange)

    // Keep onChange ref up to date
    React.useEffect(() => {
      onChangeRef.current = onChange
    }, [onChange])

    React.useEffect(() => {
      // Only call onChange if introductions actually changed (deep comparison) and we're not updating from parent
      const hasChanged = JSON.stringify(prevIntroductionsRef.current) !== JSON.stringify(introductions)
      if (hasChanged && onChangeRef.current && !isUpdatingFromParentRef.current) {
        prevIntroductionsRef.current = introductions
        onChangeRef.current(introductions)
      } else if (hasChanged) {
        // Still update the ref even if we don't call onChange
        prevIntroductionsRef.current = introductions
      }
    }, [introductions])

    // Helper function to check if a key is a temporary key (not a valid UUID)
    const isTemporaryKey = React.useCallback((key) => {
      if (!key || typeof key !== 'string') return true
      // Check if it starts with temporary prefixes
      if (key.startsWith('new-') || key.startsWith('intro-') || key.startsWith('role-')) return true
      // Check if it's a valid UUID format (8-4-4-4-12 hex characters)
      const uuidRegex = /^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/i
      return !uuidRegex.test(key)
    }, [])

    // Expose API formatter function to parent when in new mode
    React.useEffect(() => {
      if (mode === 'new' && getCurrentTeamMembers && recordId) {
        getCurrentTeamMembers(() => {
          // Transform introductions to API format (CompanyTeamEntrySchema)
          return introductions
            .filter((intro) => intro.companyId && intro.categoryId)
            .map((intro) => {
              const introKey = intro.key || ''
              const isTempKey = isTemporaryKey(introKey)
              return {
                [CompanyTeamEntryFields.contacts__id]: contactId || null,
                [CompanyTeamEntryFields.contact_accounts__id]: isTempKey ? null : introKey,
                [CompanyTeamEntryFields.contact_accounts__account_id]: intro.companyId,
                [CompanyTeamEntryFields.contact_accounts__relationship_category_id]: intro.categoryId,
                [CompanyTeamEntryFields.prog__contact_accounts__action]: isTempKey ? ACTION_TYPE_INSERT : '',
                [CompanyTeamEntryFields.prog__founder]: false
              }
            })
        })
      }
    }, [mode, getCurrentTeamMembers, recordId, introductions, contactId, isTemporaryKey])

    // Expose pending check function to parent via ref
    useImperativeHandle(
      ref,
      () => ({
        hasPendingIntroductions: () => {
          return isAddingNew || introductions.some((intro) => !intro.companyId || !intro.categoryId)
        }
      }),
      [isAddingNew, introductions]
    )

    // Handle add new introduction
    const handleAdd = useCallback(() => {
      if (readOnly) return
      const newKey = `new-${crypto.randomUUID()}`
      // Default to "Introduced By" category
      const defaultCategoryOption = categoryOptions.find((opt) => opt.value === RELATIONSHIP_CATEGORY_INTRODUCED_BY)
      const newIntro = {
        key: newKey,
        companyId: '',
        companyName: '',
        categoryId: RELATIONSHIP_CATEGORY_INTRODUCED_BY,
        categoryLabel: defaultCategoryOption?.label || defaultCategoryOption?.name || ''
      }
      // Store original data (empty state) so cancel can remove it properly
      setOriginalIntroductionData(null)
      setIsAddingNew(true)
      // Create updated introductions array first (same pattern as CompanyTeamTable)
      const updatedIntroductions = [newIntro, ...introductions]
      setIntroductions(updatedIntroductions)
      // Always set the new member as the editing row (same pattern as CompanyTeamTable)
      setEditingRowKey(newKey)
      // Auto-focus company field for new entries
      setFirstErrorFieldKey({ rowKey: newKey, fieldKey: 'company' })
    }, [readOnly, introductions, categoryOptions])

    // Handle remove
    const handleRemove = useCallback(
      async (rowKey) => {
        if (readOnly) return

        const introToRemove = introductions.find((intro) => intro.key === rowKey)
        if (!introToRemove) return

        setDeletingRowKey(rowKey)

        // In existing mode, call onRemove callback to handle API call
        if (mode === 'existing' && onRemove) {
          try {
            await onRemove(introToRemove, introductions)
            // Only remove from local state if API call succeeds
            setIntroductions((prev) => prev.filter((intro) => intro.key !== rowKey))
          } catch (error) {
            console.error('Error removing introduction:', error)
            // Don't remove from local state if API call fails
          } finally {
            setDeletingRowKey(null)
          }
        } else {
          // In new mode or no onRemove callback, just remove from local state
          setIntroductions((prev) => prev.filter((intro) => intro.key !== rowKey))
          setDeletingRowKey(null)
        }

        if (editingRowKey === rowKey) {
          setEditingRowKey(null)
          setIsAddingNew(false)
        }
      },
      [readOnly, editingRowKey, mode, onRemove, introductions]
    )

    // Handle start editing
    const startEditing = useCallback(
      (rowKey) => {
        if (readOnly) return
        // Store original data before editing starts (for all entries, so we can restore on cancel)
        const currentIntro = introductions.find((intro) => intro.key === rowKey)
        if (currentIntro) {
          setOriginalIntroductionData({ ...currentIntro })
        } else {
          setOriginalIntroductionData(null)
        }
        setEditingRowKey(rowKey)
      },
      [readOnly, introductions]
    )

    // Handle cancel editing
    const cancelEditing = useCallback(
      (rowKey) => {
        if (rowKey) {
          const isNewEntry = typeof rowKey === 'string' && rowKey.startsWith('new-')

          if (isNewEntry) {
            setIntroductions((prev) => {
              const next = prev.filter((intro) => intro.key !== rowKey)
              if (next.length === 0) {
                cancelledDraftToEmptyRef.current = true
              }
              onChange?.(next)
              return next
            })
            setIsAddingNew(false)
          } else if (originalIntroductionData) {
            setIntroductions((prev) => prev.map((intro) => (intro.key === rowKey ? originalIntroductionData : intro)))
          }
        }
        setEditingRowKey(null)
        setSavingRowKey(null)
        setOriginalIntroductionData(null)
      },
      [originalIntroductionData, onChange]
    )

    // Wrapper for FormTable's onCancel (which doesn't pass rowKey)
    // FormTable calls onCancel without arguments, so we use editingRowKey from state
    const handleCancel = useCallback(() => {
      if (editingRowKey) {
        cancelEditing(editingRowKey)
      }
    }, [editingRowKey, cancelEditing])

    // Handle field change
    const handleFieldChange = useCallback(
      (rowKey, field, value) => {
        setIntroductions((prev) => {
          return prev.map((intro) => {
            if (intro.key !== rowKey) return intro

            if (field === 'company') {
              // CompanySelect can pass either a string (ID) or an object with value/label
              const companyId = typeof value === 'string' ? value : value?.value || value?.id || ''
              const companyName = typeof value === 'object' && value ? value.label || value.name || '' : ''

              return {
                ...intro,
                companyId,
                companyName
              }
            } else if (field === 'category') {
              const categoryId = typeof value === 'object' ? value.value : value
              const categoryOption = categoryOptions.find((opt) => opt.value === categoryId)
              const categoryLabel = categoryOption?.label || categoryOption?.name || ''
              return {
                ...intro,
                categoryId,
                categoryLabel
              }
            }
            return intro
          })
        })
      },
      [categoryOptions]
    )

    // Handle save
    const handleSave = useCallback(
      (rowKey) => {
        const intro = introductionsRef.current.find((i) => i.key === rowKey)
        if (!intro) {
          cancelEditing(rowKey)
          return false
        }

        // Clear previous validation errors for this record
        const errorKey = `${rowKey}-errors`
        setValidationErrors((prev) => ({
          ...prev,
          [errorKey]: {}
        }))
        setFirstErrorFieldKey(null) // Clear previous focus state

        // Validate the record
        const validationResult = validateAndShowErrors(intro, introductionValidationRules)

        // If there are validation errors, highlight fields and focus first error
        if (!validationResult.isValid) {
          // Map validation errors from fieldKey (companyId/categoryId) to field key (company/category) for InputManager
          const mappedErrors = {}
          if (validationResult.errors.companyId) {
            mappedErrors.company = validationResult.errors.companyId
          }
          if (validationResult.errors.categoryId) {
            mappedErrors.category = validationResult.errors.categoryId
          }

          // Update validation errors state to trigger field highlighting
          setValidationErrors((prev) => ({
            ...prev,
            [errorKey]: mappedErrors
          }))

          // Identify first error field based on priority order (using field keys, not fieldKeys)
          const fieldPriority = ['company', 'category']
          const firstErrorField = fieldPriority.find((field) => mappedErrors[field])

          if (firstErrorField) {
            setFirstErrorFieldKey({ rowKey: rowKey, fieldKey: firstErrorField })
          }

          // Toast is already shown by validateAndShowErrors
          return false
        }

        // Check for duplicates before saving
        const otherIntroductions = introductionsRef.current.filter((i) => i.key !== rowKey)
        if (isDuplicateIntroduction(intro, otherIntroductions, null)) {
          toast.warning('An introduction with this Company and Role already exists', TOAST_CONFIG)
          return false
        }

        // All validations passed - save
        setSavingRowKey(rowKey)
        setEditingRowKey(null)
        setIsAddingNew(false)
        setFirstErrorFieldKey(null) // Clear focus state on successful save
        setOriginalIntroductionData(null) // Clear original data on successful save

        // Call onSave callback if provided (for existing mode to trigger API calls)
        if (onSave && mode === 'existing') {
          onSave(intro, introductionsRef.current)
        }

        setSavingRowKey(null)
        return true
      },
      [cancelEditing, introductionValidationRules, onSave, mode]
    )

    // Auto-focus first error field when validation fails
    useEffect(() => {
      if (firstErrorFieldKey && editingRowKey === firstErrorFieldKey.rowKey) {
        // Small delay to ensure DOM is updated
        const timeoutId = setTimeout(() => {
          const fieldKey = firstErrorFieldKey.fieldKey

          // Find the table row using Ant Design's rowKey attribute
          const tableRow = document.querySelector(`tr[data-row-key="${firstErrorFieldKey.rowKey}"]`)
          if (tableRow) {
            // Find all cells in the row (excluding actions column which is last)
            const cells = Array.from(tableRow.querySelectorAll('td'))

            // Map field keys to column dataIndex (matching the column order)
            const fieldToColumnMap = {
              company: 'company',
              category: 'category'
            }

            // Find the cell that contains the field we want to focus
            const columnIndex = Object.keys(fieldToColumnMap).indexOf(fieldKey)
            if (columnIndex !== -1) {
              // Filter cells that have inputs (not action buttons)
              const dataIndexCells = cells.filter((cell) => {
                return (
                  cell.querySelector('input, textarea, .ant-select-selector, .ant-select') &&
                  !cell.querySelector('button[class*="ant-btn"]')
                )
              })

              if (dataIndexCells[columnIndex]) {
                const targetCell = dataIndexCells[columnIndex]
                const input = targetCell.querySelector('input, textarea, .ant-select-selector, .ant-select')
                if (input) {
                  // For Ant Design Select, focus the selector
                  if (input.classList.contains('ant-select')) {
                    const selector = input.querySelector('.ant-select-selector')
                    if (selector) {
                      selector.focus()
                      setTimeout(() => selector.click(), 50)
                    }
                  } else {
                    input.focus()
                  }
                }
              }
            }
          }
        }, 200) // Delay to ensure DOM updates are complete

        return () => clearTimeout(timeoutId)
      }
    }, [firstErrorFieldKey, editingRowKey])

    // Columns configuration
    const columns = useMemo(() => {
      return [
        {
          ...getColumnConfig('company', { label: 'Company', required: true }, 300, 300),
          render: (text, record) => {
            const isEditing = editingRowKey === record.key
            const companyId = record.companyId
            const firstErrorField = firstErrorFieldKey?.rowKey === record.key ? firstErrorFieldKey.fieldKey : null
            const errorKey = `${record.key}-errors`

            // If we have companyName from API (prog__account__name), pass as object so InputManager displays it directly
            // Otherwise pass just the ID string (CompanySelect resolves it through its search results)
            const companyValue =
              companyId && record.companyName
                ? { value: companyId, label: record.companyName, name: record.companyName }
                : companyId || ''

            const companyFieldConfig = {
              key: 'company',
              label: 'Company',
              type: FieldTypeEnum.COMPANY,
              value: companyValue,
              editable: isEditing,
              required: true,
              placeholder: 'Search companies...',
              autoFocus: firstErrorField === 'company',
              hyperlinked: true,
              hyperlinkModule: 'companies',
              getRecordId: (value) => value?.value || value?.id || null
            }

            if (!isEditing) {
              return (
                <div
                  onClick={readOnly ? undefined : () => startEditing(record.key)}
                  className={readOnly ? '' : 'cursor-pointer'}
                >
                  <InputManager
                    fields={[companyFieldConfig]}
                    inputMode='read'
                    labelPosition='none'
                    onChange={(key, value) => handleFieldChange(record.key, key, value)}
                    validationErrors={validationErrors[errorKey]}
                    textClassName='text-sm'
                  />
                </div>
              )
            }

            return (
              <InputManager
                fields={[companyFieldConfig]}
                inputMode='edit'
                labelPosition='none'
                onChange={(key, value) => handleFieldChange(record.key, key, value)}
                validationErrors={validationErrors[errorKey]}
                textClassName='text-sm'
              />
            )
          }
        },
        {
          ...getColumnConfig('category', { label: 'Role', required: true }, 200, 200),
          render: (text, record) => {
            const isEditing = editingRowKey === record.key
            const categoryId = record.categoryId
            const firstErrorField = firstErrorFieldKey?.rowKey === record.key ? firstErrorFieldKey.fieldKey : null
            const errorKey = `${record.key}-errors`

            const categoryFieldConfig = {
              key: 'category',
              label: 'Role',
              type: FieldTypeEnum.SELECT,
              value: categoryId || '',
              editable: isEditing,
              required: true,
              options: categoryOptions,
              placeholder: 'Select role...',
              allowClear: false,
              autoFocus: firstErrorField === 'category'
            }

            if (!isEditing) {
              return (
                <div
                  onClick={readOnly ? undefined : () => startEditing(record.key)}
                  className={readOnly ? '' : 'cursor-pointer'}
                >
                  <InputManager
                    fields={[categoryFieldConfig]}
                    inputMode='read'
                    labelPosition='none'
                    onChange={(key, value) => handleFieldChange(record.key, key, value)}
                    validationErrors={validationErrors[errorKey]}
                    textClassName='text-sm'
                  />
                </div>
              )
            }

            return (
              <InputManager
                fields={[categoryFieldConfig]}
                inputMode='edit'
                labelPosition='none'
                onChange={(key, value) => handleFieldChange(record.key, key, value)}
                validationErrors={validationErrors[errorKey]}
                textClassName='text-sm'
              />
            )
          }
        }
      ]
    }, [
      editingRowKey,
      readOnly,
      categoryOptions,
      handleFieldChange,
      startEditing,
      firstErrorFieldKey,
      validationErrors,
      introductions
    ])

    // Filter out "Sourced By" entries from display (but keep them in data for callbacks)
    // Exception: Show "Sourced By" entries that are currently being edited
    const displayedIntroductions = useMemo(() => {
      return introductions.filter(
        (intro) => intro.categoryId !== RELATIONSHIP_CATEGORY_SOURCED_BY || editingRowKey === intro.key
      )
    }, [introductions, editingRowKey])

    return (
      <FormTable
        title={title}
        dataSource={displayedIntroductions}
        columns={columns}
        addButtonText=''
        addButtonIcon={faPlus}
        addButtonTestId='add-introduction-button'
        emptyText='No introductions added'
        isAddingNew={isAddingNew}
        onAdd={readOnly ? undefined : handleAdd}
        className='compact-introductions-table'
        includeActions={readOnly ? false : true}
        actionsWidth={60}
        mode={mode}
        editingRowKey={editingRowKey}
        savingRowKey={savingRowKey}
        deletingRowKey={deletingRowKey}
        onRemove={handleRemove}
        onSave={handleSave}
        onCancel={handleCancel}
        validateRecord={null}
        useDeleteConfirm={false}
      />
    )
  }
)

IntroductionsTable.displayName = 'IntroductionsTable'
